import { useEffect, useRef } from "react";

import { createFileRoute } from "@tanstack/react-router";

import { ChatLayout } from "@/components/messages/ChatLayout";
import { Spinner } from "@/components/ui/spinner";
import { useChatContext } from "@/contexts/ChatContext";
import { useChainOfThought } from "@/contexts/ChainOfThoughtContext";
import { useQualificationLifecycle } from "@/contexts/QualificationLifecycleContext";
import { useScrollEngine } from "@/contexts/ScrollEngineContext";
import { resolveActiveClarificationForm } from "@/lib/qualification-lifecycle/clarification-anchor";
import { resolveActiveAttemptQuestionMessageId } from "@/lib/qualification-lifecycle/active-attempt-anchor";
import { isQuestionUserMessage } from "@/lib/qualification-lifecycle/metadata-part";
import { ensureConversationContentHydrated } from "@/lib/queries/conversation-content";
import { prefetchConversationFeedbacks } from "@/lib/queries/conversation-feedbacks";
import {
  isActiveTranscriptFollowCycle,
  resolveMessageTarget,
  waitForElement,
} from "@/lib/scroll";

function ConversationPendingView() {
  return (
    <div className="relative flex flex-col min-w-0 h-full overflow-hidden">
      <div className="flex flex-1 min-h-0 items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    </div>
  );
}

function ConversationView() {
  const { cId } = Route.useParams();
  const { questionId } = Route.useSearch();
  const { dispatch, setActiveQuestionId, setFollowPinned } = useScrollEngine();
  const lastScrolledTargetRef = useRef<string | null>(null);
  const initialScrollDoneForConvRef = useRef<Set<string>>(undefined);
  if (initialScrollDoneForConvRef.current === undefined) {
    initialScrollDoneForConvRef.current = new Set();
  }
  const lifecycleFollowInitRef = useRef<string | null>(null);
  const {
    messages,
    isLoadingConversation,
    gatekeeperPrecisionsForceOpen,
    streamingMessageId,
  } = useChatContext();
  const lifecycle = useQualificationLifecycle();
  const activeClarificationForm = resolveActiveClarificationForm(
    messages,
    lifecycle.state
  );
  const { isChainOfThoughtActive } = useChainOfThought();
  const isFollowCycleActive = isActiveTranscriptFollowCycle({
    gatekeeperPrecisionsForceOpen,
    streamingMessageId,
    isChainOfThoughtActive,
  });

  useEffect(() => {
    setActiveQuestionId(cId, null);
    lastScrolledTargetRef.current = null;
    lifecycleFollowInitRef.current = null;
  }, [cId, setActiveQuestionId]);

  useEffect(() => {
    if (isLoadingConversation || messages.length === 0) {
      return;
    }

    if (activeClarificationForm) {
      return;
    }

    const hasExplicitQuestionId =
      questionId != null && messages.some((msg) => msg.id === questionId);

    if (hasExplicitQuestionId) {
      const resolvedId = resolveMessageTarget(questionId, messages);
      if (lastScrolledTargetRef.current === resolvedId) {
        return;
      }

      const isFirstScrollForConversation =
        !initialScrollDoneForConvRef.current.has(cId);
      const conversationIdAtStart = cId;
      let cancelled = false;

      waitForElement(`[data-message-id="${resolvedId}"]`).then((el) => {
        if (cancelled) {
          return;
        }
        if (el) {
          lastScrolledTargetRef.current = resolvedId;
          if (isFirstScrollForConversation) {
            initialScrollDoneForConvRef.current.add(conversationIdAtStart);
          }
          dispatch({
            type: "scroll-to-message",
            conversationId: conversationIdAtStart,
            messageId: resolvedId,
            ...(isFirstScrollForConversation
              ? { behavior: "instant" as const, highlight: "none" as const }
              : {}),
          });
        } else if (import.meta.env.DEV) {
          console.warn(
            `Message element ${resolvedId} not found; scroll skipped`
          );
        }
      });

      return () => {
        cancelled = true;
      };
    }

    if (isFollowCycleActive) {
      if (lifecycleFollowInitRef.current === cId) {
        return;
      }
      lifecycleFollowInitRef.current = cId;
      setFollowPinned(cId, true);
      const activeAttemptQuestionId = resolveActiveAttemptQuestionMessageId(
        messages,
        lifecycle.state
      );
      if (activeAttemptQuestionId) {
        setActiveQuestionId(cId, activeAttemptQuestionId);
      }
      return;
    }

    let targetQuestionId: string | null = null;
    const lastQuestionMessage = [...messages]
      .reverse()
      .find(isQuestionUserMessage);
    if (lastQuestionMessage) {
      targetQuestionId = lastQuestionMessage.id;
    }

    if (!targetQuestionId) {
      return;
    }

    const resolvedId = resolveMessageTarget(targetQuestionId, messages);
    if (lastScrolledTargetRef.current === resolvedId) {
      return;
    }

    const isFirstScrollForConversation =
      !initialScrollDoneForConvRef.current.has(cId);
    const conversationIdAtStart = cId;
    let cancelled = false;

    waitForElement(`[data-message-id="${resolvedId}"]`).then((el) => {
      if (cancelled) {
        return;
      }
      if (el) {
        lastScrolledTargetRef.current = resolvedId;
        if (isFirstScrollForConversation) {
          initialScrollDoneForConvRef.current.add(conversationIdAtStart);
        }
        dispatch({
          type: "scroll-to-message",
          conversationId: conversationIdAtStart,
          messageId: resolvedId,
          ...(isFirstScrollForConversation
            ? { behavior: "instant" as const, highlight: "none" as const }
            : {}),
        });
      } else if (import.meta.env.DEV) {
        console.warn(`Message element ${resolvedId} not found; scroll skipped`);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    isLoadingConversation,
    questionId,
    messages,
    cId,
    dispatch,
    setFollowPinned,
    isFollowCycleActive,
    activeClarificationForm,
    lifecycle.state,
    setActiveQuestionId,
  ]);

  return <ChatLayout />;
}

export const Route = createFileRoute("/_app/conversations/$cId")({
  component: ConversationView,
  pendingComponent: ConversationPendingView,
  pendingMs: 0,
  pendingMinMs: 0,
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    await ensureConversationContentHydrated(queryClient, params.cId);
    await prefetchConversationFeedbacks(params.cId);
  },
  validateSearch: (
    search: Record<string, unknown>
  ): { questionId?: string } => {
    return {
      questionId:
        typeof search.questionId === "string" ? search.questionId : undefined,
    };
  },
});
